/**
 * ``useCreateOrder`` — create a single-product order atomically and idempotently.
 *
 * ``POST /api/orders/`` re-prices server-side, debits the currency ledger and creates the
 * order in one ``transaction.atomic()``. The frontend supplies only intent and a stable
 * ``Idempotency-Key`` so a retried/double-tapped submit can never create two orders or
 * double-debit (Global Constraints; ТЗ §18).
 *
 * The idempotency key is generated once per *attempt* (a given product+spend signature) and
 * reused on retry; changing the spend starts a new attempt.
 */
import { ApiError } from "@shared/api";
import type { OrderDetail } from "@entities/order";
import { useCallback, useMemo, useRef, useState } from "react";

import { createProductOrder } from "./api";
import { newIdempotencyKey } from "./lib";
import type { CreateOrderInput } from "./model";

export interface CreateOrderState {
  readonly order: OrderDetail | null;
  readonly error: ApiError | null;
  readonly isSubmitting: boolean;
  /** Submit the order; resolves with the created order or rejects with {@link ApiError}. */
  readonly submit: () => Promise<OrderDetail>;
}

/**
 * @param input - The order to create, or ``null`` when not yet orderable.
 * @param attemptSignature - Stable signature of the current attempt; the idempotency key is
 *   regenerated whenever this changes.
 */
export function useCreateOrder(
  input: CreateOrderInput | null,
  attemptSignature: string,
): CreateOrderState {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // One idempotency key per attempt; regenerated when the signature changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const idempotencyKey = useMemo(() => newIdempotencyKey(), [attemptSignature]);

  const inputRef = useRef(input);
  inputRef.current = input;
  const keyRef = useRef(idempotencyKey);
  keyRef.current = idempotencyKey;

  const submit = useCallback(async (): Promise<OrderDetail> => {
    const current = inputRef.current;
    if (current === null) {
      throw new ApiError(0, "Order is not ready", null);
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const created = await createProductOrder(current, keyRef.current);
      setOrder(created);
      return created;
    } catch (err: unknown) {
      const apiError =
        err instanceof ApiError ? err : new ApiError(0, "Network error", err);
      setError(apiError);
      throw apiError;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { order, error, isSubmitting, submit };
}
